/**
 * The data-state grammar in code (UX-003 §3). Plain words for states, the
 * withheld test, templated reasons, and age formatting. State codes are stored
 * in their ruled form and shown in plain words — the mapping is fixed here so a
 * state means the same thing on every screen.
 */
import type { Figure, FigureState } from "@solarkey/api-client";

type Reason = NonNullable<Figure["reason"]>;

/** The plain word for a figure state (UX-003 §3.8). */
export const STATE_WORD: Record<string, string> = {
  MEASURED: "Measured",
  PROVISIONAL: "Provisional",
  CANNOT_CHECK: "Cannot check",
  NOT_MEASURED: "Not measured",
  NOT_APPLICABLE: "Not applicable",
};

export function stateWord(state: FigureState | string): string {
  return STATE_WORD[state] ?? String(state);
}

/** The plain word for an energy basis chip (UX-003 §3.8). */
export function basisLabel(basis: string | null | undefined): string | null {
  if (!basis) return null;
  switch (basis) {
    case "METER":
      return "Meter";
    case "INVERTER_DERIVED":
      return "Inverter-derived";
    default:
      return basis;
  }
}

/**
 * A figure is withheld when it carries no value — its state word is shown in
 * place of a number. Rendering 0 here is a client bug (UX-003 §3.1).
 */
export function isWithheld(figure: Figure): boolean {
  if (figure.value === null || figure.value === undefined) return true;
  return (
    figure.state === "CANNOT_CHECK" ||
    figure.state === "NOT_MEASURED" ||
    figure.state === "NOT_APPLICABLE"
  );
}

/**
 * A readable sentence for a withheld/marked figure, from its templated reason.
 * No free text ever enters a reason — this maps known templates and otherwise
 * humanises the template key. Full localisation comes from the glossary later.
 */
export function reasonText(reason: Reason | null | undefined): string | null {
  if (!reason?.template) return null;
  const p = (reason.params ?? {}) as Record<string, unknown>;
  switch (reason.template) {
    case "PROVISIONAL_COVERAGE":
      return `Coverage ${String(p["pct"] ?? "?")}% of intervals, below the ${String(p["floor"] ?? "?")}% floor.`;
    case "NO_TARIFF":
      return "No tariff loaded for this plant.";
    case "NOT_MEASURED":
      return "Not measured for this period.";
    case "EQUIPMENT_AVAIL_NOT_WIRED":
      // Say what is missing and who owes it. "Not measured yet" left a reader with
      // no idea whether this was a bug, a setting, or someone else's work.
      return (
        "Needs equipment-only downtime per interval, which the Availability engine "
        + "supplies over Interface B — and it has sent none. The platform will not "
        + "estimate it: using total downtime would blame equipment for grid outages, "
        + "curtailment and maintenance alike."
      );
    case "PATH_DOWN":
      return "Data path down. Values shown are last known.";

    /* ── Engines and seams that have not produced yet ── */
    // A panel whose seam is built but whose engine has not been connected to it.
    // Distinct from NOT_MEASURED_ENGINE_ABSENT, which is a row an engine OWNS and
    // has not produced; this is a panel with nothing behind it yet.
    //
    // It replaces the literal "wired-pending-engine", which was not a template at
    // all: with no case to match it fell through to humanise(), and twenty-five
    // panels showed the reader the tooltip "Wired-pending-engine." — a developer's
    // note to themselves, on the glass, in front of a customer.
    case "NOT_MEASURED_CONDITION_NOT_TIMESTAMPED":
      return "This rule is evaluated fresh on each read and not stored, so when the condition began is not recorded.";
    case "CANNOT_CHECK_DETECTIONS_UNAVAILABLE":
      return "The detection read failed, so open alerts could not be listed. This is not an all-clear.";
    case "NOT_MEASURED_PENDING_ENGINE":
      return "Not measured yet — this panel is built, but the engine behind it is not connected.";
    case "NOT_MEASURED_ENGINE_ABSENT":
      return p["engine"]
        ? `Not measured — the ${String(p["engine"])} engine has not produced this row.`
        : p["engines"]
          ? `Not measured — these engines have not produced: ${String(p["engines"])}.`
          : "Not measured — the engine that owns this row has not produced it.";
    case "CANNOT_CHECK_GRID_EVENT_EXCLUSION_NOT_WIRED":
      return "Grid-adjusted PR is withheld — grid-event exclusion is not wired, so the correction cannot be applied.";
    case "CANNOT_CHECK_PR_VARIANT_UNSUPPORTED":
      return `This PR variant is not served${p["variant"] ? ` (${String(p["variant"])})` : ""}.`;
    case "CANNOT_CHECK_PR_VARIANT_ROLLUP":
      return "A PR variant is not rolled up across sites — a ratio of ratios would not be the ruled figure.";
    case "INTERVENTION_LIFECYCLE_NOT_WIRED":
      return "Not measured — no intervention lifecycle engine records this lane yet.";
    case "NOT_MEASURED_NO_FINDINGS_STORE":
    case "NO_FINDINGS_STORE":
      return "Not measured — no findings store is connected, so open findings cannot be counted.";
    case "NOT_MEASURED_ENGINE_HAS_NOT_RUN":
      return "Not measured — the evaluation engines have not produced a result for this site yet.";

    /* ── Registry values nobody has authored ── */
    case "NOT_MEASURED_BLOCK_LABEL_NOT_AUTHORED":
      return "Not recorded — no block membership is authored in the registry.";
    case "NOT_MEASURED_WARRANTY_DATES_NOT_AUTHORED":
      return "Not recorded — no warranty dates are authored in the registry.";
    case "NOT_MEASURED_FIRMWARE_NOT_IN_REGISTRY":
      return "Not recorded — the registry holds no firmware field.";
    case "NOT_MEASURED_NO_NAMEPLATE_RATING":
      return "Not recorded — this device carries no plated rating.";
    case "NOT_MEASURED_REGISTRY_FIELD_NOT_AUTHORED":
      return "Not recorded — this registry field has not been authored.";
    case "NOT_MEASURED_REPLACEMENT_LINEAGE_NOT_RECORDED":
      return "Not measured — replacement lineage is not recorded, so a count cannot be told from none.";
    case "NOT_MEASURED_METER_GROUPS_NOT_AUTHORED":
      return "Not recorded — no meter-to-inverter mapping is authored.";
    case "NOT_MEASURED_NO_TOPOLOGY_EDGES":
      return "Not recorded — no topology edges are loaded for this site.";
    case "CANNOT_CHECK_NO_SITE_COORDINATES":
      return "Cannot check — the registry holds no coordinates for these sites, so they cannot be placed on a map.";

    /* ── Live path and stores ── */
    case "CANNOT_CHECK_NO_VALUE":
      // Generic: the figure is absent but the caller does not know WHY (e.g. the
      // fleet-map power cell), so a specific cause would misstate it.
      return "Cannot check — no reading is available for this site.";
    case "CANNOT_CHECK_NO_LIVE_PATH":
      return "Cannot check — no live data path reached this site.";
    case "CANNOT_CHECK_TELEMETRY_STORE_UNREACHABLE":
    case "EVENT_STORE_UNREACHABLE":
    case "CANNOT_CHECK_EVENT_STORE_UNREACHABLE":
      return "Cannot check — the store could not be read, so an empty result cannot be read as nothing.";
    case "CANNOT_CHECK_TIMESERIES_UNREADABLE":
      return "Cannot check — the timeseries store could not be read.";
    case "NOT_MEASURED_NO_DEVICE_REPORTS_SIGNAL":
      return "Not measured — no device on this site reports this signal.";
    case "NOT_MEASURED_SIGNAL_NOT_IN_CATALOG":
      return "Not measured — this signal is not in the metric catalogue.";
    case "CANNOT_CHECK_SIGNAL_INVENTORY_UNAVAILABLE":
      return "Cannot check — the signal inventory could not be read, so complete coverage cannot be claimed.";
    case "NOT_MEASURED_NO_LOGGER_SIGNAL":
      return "Not measured — the logger emits no signal of its own; its packets are only visible as device telemetry.";
    case "NOT_MEASURED_NO_PLATFORM_HEARTBEAT":
      return "Not measured — the platform publishes no heartbeat; the request clock is not a packet.";
    case "NOT_APPLICABLE_DEVICE_NOT_INSTRUMENTED":
    case "DEVICE_NOT_INSTRUMENTED":
      return "Not applicable — this device is on the registry but carries no instrument.";

    /* ── Meters, flow and basis ── */
    case "CANNOT_CHECK_NO_EXPORT_METER_REGISTERED":
      return "Cannot check — no export meter is registered at this site.";
    case "CANNOT_CHECK_NO_RECENT_METER_POWER":
      return "Cannot check — no recent reading from the meter.";
    case "CANNOT_CHECK_METER_SHOWS_IMPORT":
      return "Cannot check — the meter is reading import, so an evacuation loss cannot be stated.";
    case "CANNOT_CHECK_METER_EXCEEDS_INVERTER_SUM":
      return "Cannot check — metered export exceeds the inverter sum, so the difference is not a loss.";
    case "NOT_MEASURED_NO_AUXILIARY_METER":
      return "Not measured — no auxiliary or station-service meter is registered.";
    case "NOT_APPLICABLE_NO_STORAGE_REGISTERED":
      return "Not applicable — the registry holds no storage device at this site.";
    case "NOT_MEASURED_NO_PER_DEVICE_ENERGY_BASIS":
    case "NO_PER_DEVICE_ENERGY_BASIS":
      return "Not measured — no designated energy basis exists for a single device.";
    case "CANNOT_CHECK_NO_DESIGNATED_BASIS":
      return "Cannot check — the designated energy basis is not stated for this scope.";
    case "CANNOT_CHECK_STRING_BASIS_NOT_ON_RECORD":
      return "Cannot check — no string-measurement basis is on record for this site.";
    case "NOT_MEASURED_NO_STRING_SERIES_ENDPOINT":
      return "Not measured — no string-level series is served, so a per-string current cannot be stated.";

    /* ── Roll-ups and panels with no seam ── */
    case "CANNOT_CHECK_NO_FLEET_ROLLUP":
      return "Cannot check — no server-computed fleet roll-up is served; the browser must not derive a ruled KPI.";
    case "CANNOT_CHECK_NO_GROUP_ROLLUP":
      return "Cannot check — no server-computed group roll-up is served.";
    case "CANNOT_CHECK_NO_INTERVENTION_COUNT":
      return "Cannot check — open interventions could not be counted for this site.";
    case "CANNOT_CHECK_NO_VALUE_AT_STAKE":
    case "CANNOT_CHECK_NO_VALUE_AT_STAKE_ENGINE":
      return "Cannot check — no value engine exists, so energy or money at stake cannot be stated.";
    case "NOT_MEASURED_NO_KPI_SERIES_ENDPOINT":
      return "Not measured — no per-day KPI series is served for this scope.";
    case "NOT_MEASURED_NO_DAILY_LADDER_SERIES":
      return "Not measured — the daily loss ladder is not served as a series.";
    case "NOT_MEASURED_NO_DOWNTIME_BY_CAUSE":
      return "Not measured — downtime by device and cause is not served.";
    case "NOT_MEASURED_NO_PERFORMANCE_INDEX":
      return "Not measured — no reference-power model produces a performance index.";
    case "NOT_MEASURED_NO_CASCADE_HEADLINE":
      return "Not measured — the cascade headline is not served and must not be re-derived here.";
    /* ── Home tiles and the fleet roll-up (screen 01) ── */
    case "PROVISIONAL_SITE_COVERAGE":
      return `Provisional — this figure covers ${String(p["sites"] ?? "?")} of ${String(p["sites_total"] ?? "?")} sites.`;
    case "CANNOT_CHECK_NO_SITE_IN_SCOPE_COULD_BE_READ":
      return "Cannot check — not one site in scope could be read, so there is nothing to total.";
    case "CANNOT_CHECK_NO_FLEET_RATIO_DENOMINATOR":
      return "Cannot check — no site in scope supplied the denominator this ratio needs, so the fleet figure cannot be formed.";
    case "CANNOT_CHECK_NO_REGISTERED_AC_CAPACITY":
      return "Cannot check — no AC rating is registered for the sites in scope, so there is nothing to take a percentage of.";
    case "NOT_MEASURED_NO_SITE_IN_SCOPE":
      return "Not measured — there is no site in scope.";
    case "NOT_MEASURED_LIVE_ONLY_FIGURE_OVER_HISTORY_PERIOD":
      return "Not measured — this figure is computed over the live day only; a completed day stores nothing it could be read from.";
    case "CANNOT_CHECK_QUIET_DATA_PATH_UNVERIFIED":
      return "Cannot check — the data path could not be verified for every site, so quiet cannot be claimed; silence from a site we did not reach is not good news.";
    /* Emitted by kpi.py and _live_day.py and never worded until Home surfaced them. */
    case "CANNOT_CHECK_NO_LIVE_SOURCE":
      return "Cannot check — nothing on the live path supplies this figure.";
    case "CANNOT_CHECK_NO_LIVE_INTERVALS_TODAY":
      return "Cannot check — no interval has been recorded for this site today.";
    case "NOT_MEASURED_NO_TELEMETRY_SOURCE":
      return "Not measured — this site has no telemetry source, so there is nothing to read.";
    case "NOT_MEASURED_SITE_DARK":
      return "Site dark — the datalogger was down for this period, so no telemetry arrived and no day was built. Nothing is pending for it.";

    /* ── Fleet roll-ups and the fleet series (Home, screen 01) ── */
    case "CANNOT_CHECK_NO_INTERVALS_IN_WINDOW":
      return "Cannot check — this site produced no intervals in the window, so it is not in the fleet figure.";
    case "CANNOT_CHECK_NO_DESIGNATED_POA_SENSOR":
      return "Cannot check — no plane-of-array sensor is designated for this site, so there is nothing to expect against.";
    case "PROVISIONAL_FLEET_COVERAGE":
      return `Provisional — this figure covers ${String(p["reporting"] ?? "?")} of ${String(p["total"] ?? "?")} sites.`;
    case "CANNOT_CHECK_NO_FLEET_PR_REFERENCE":
      return "Cannot check — the fleet PR could not be established, so no deviation from it can be stated.";
    case "NOT_MEASURED_NO_POTENTIAL_ROW":
      return "Not measured — the cascade carries no potential, so a share of it cannot be stated; a share of an unknown whole is not a number.";
    case "NOT_MEASURED_ROW_CARRIES_NO_FIGURE":
      return "Not measured — this row carries no figure, so it has no share of potential.";
    case "NOT_MEASURED_NO_CASCADE_ROW_SHARE":
      return "Not measured — a cascade row carries no share of potential, and a share computed here would not be the ruled figure.";
    case "CANNOT_CHECK_NO_CASCADE_SITE_SPLIT":
      return "Cannot check — no per-site split of a cascade row is served, so the sites behind this row cannot be named or ranked.";
    case "CANNOT_CHECK_NO_CASCADE_SITE_COUNT":
      return "Cannot check — no site count or coverage count travels with the cascade.";
    case "CANNOT_CHECK_CUSTOM_RANGE_NOT_IN_CONTRACT":
      return "Cannot check — a custom range cannot be requested here: this endpoint takes a period label, not a from/to pair.";
    case "CANNOT_CHECK_LIVE_DAY_HAS_NO_COMPLETED_KPI":
      return "Cannot check — a daily KPI exists only once the day is over and the evaluator has run; today is served live instead.";
    case "CANNOT_CHECK_NO_FLEET_ROLLUP_FOR_RATIO":
      return "Cannot check — this figure is a ratio or a per-capacity value, which cannot be summed across sites; the weighted roll-up is the engine's to compute.";
    case "NOT_MEASURED_NO_DAILY_SUMMARY_FOR_DAY":
      return "Not measured — no daily summary was produced for this day, so the record has a gap here.";
    case "NOT_MEASURED_KPI_NOT_COMPUTED_FOR_DAY":
      return "Not measured — the day was evaluated but this KPI was not computed for it.";

    /* ── Configuration: whole tables that do not exist yet (screen 07) ── */
    case "CANNOT_CHECK_NO_SITE_ID":
      return "Cannot check — no site is selected, so nothing can be resolved for one.";
    case "NOT_APPLICABLE_FIXED_BY_RULING":
      return "Not applicable — this is fixed by the ruling, not resolved per site.";
    case "NOT_MEASURED_NO_PACK_VALIDATION_RESULTS":
      return "Not measured — the resolve returns parameters only; no validation results are served.";
    case "NOT_MEASURED_NO_INTERCONNECTION_TERMS":
      return "Not measured — there is no interconnection-terms table, so export and ramp limits are not on record.";
    case "NOT_MEASURED_NO_TENANT_POLICY_TABLE":
      return "Not measured — there is no tenant policy table, so this threshold is not on record.";
    case "NOT_MEASURED_NO_THRESHOLD_TABLE":
      return "Not measured — there is no threshold table, so this limit is not on record.";
    /* ── People and access: what §06 shows that has no source (screen 06) ── */
    case "NOT_MEASURED_NO_ORGANISATION_MEMBERSHIP":
      return "Not measured — nothing links a person to an organisation, so the organisation on a seat is not on record.";
    case "NOT_MEASURED_NO_RELATIONSHIP_GRAPH":
      return "Not measured — there is no relationship graph, so a seat's scope has no relationship-wide denominator; the tenant's own site total is served instead.";
    case "NOT_MEASURED_NO_PERSON_CERTIFICATE_RECORD":
      return "Not measured — certifications are held against a crew, not a person, so none are on record for this seat.";
    case "NOT_MEASURED_NO_DEVICE_ENROLMENT_STORE":
      return "Not measured — there is no device-enrolment store, so devices and last sync are not on record.";
    case "NOT_MEASURED_NO_IDENTITY_PROVIDER_RECORD":
      return "Not measured — no identity provider is recorded against a person, so the sign-in route cannot be stated.";
    case "NOT_MEASURED_NO_MFA_RECORD":
      return "Not measured — multi-factor state is not recorded against a person.";
    case "CANNOT_CHECK_NO_ACTIVITY_TRAIL":
      return "Cannot check — the change ledger holds no entries for this person, and it would record actions rather than presence; last sign-in is served instead and does not imply activity since.";
    case "CANNOT_CHECK_ROLE_CATALOGUE_UNREADABLE":
      return "Cannot check — the role catalogue could not be read, so this seat's lenses and authority are withheld rather than assumed.";

    /* ── Report packs: sections with no source (screen 08) ── */
    case "NOT_APPLICABLE_NO_CONTRACT_REGISTER":
      return "Not applicable — there is no contract register, so attainment cannot be stated per contract.";
    case "NOT_APPLICABLE_NO_CLAIMS_STORE":
      return "Not applicable — no claims are stored, so none can be reported beside the curtailment they would follow.";
    case "NOT_MEASURED_NO_AVAILABILITY_PARTY_ATTRIBUTION":
      return "Not measured — availability is not attributed to a party; that needs a responsibility mapping no record carries.";
    case "NOT_APPLICABLE_NO_RECOVERY_ENGINE":
      return "Not applicable — no recovery engine exists, so recovered energy and value are not stated.";
    case "NOT_APPLICABLE_NO_REPORT_TEMPLATE_STORE":
      return "Not applicable — no report templates are stored, so none can be listed.";
    case "NOT_APPLICABLE_NO_REPORT_SCHEDULE_STORE":
      return "Not applicable — no report schedules are stored, so no next run can be stated.";
    case "NOT_APPLICABLE_NO_REPORT_RUN_HISTORY":
      return "Not applicable — report runs are not stored, so there is no history to show; a pack is composed on request.";
    case "NOT_APPLICABLE_NO_REPORT_APPROVAL_STORE":
      return "Not applicable — no approval is recorded against a pack and there is no recipient list, so a pack cannot be marked approved or sent.";
    case "CANNOT_CHECK_NO_FLEET_DATA_PATH":
      return "Cannot check — the data path is served per site, so it cannot answer for a whole scope at once.";

    case "NOT_MEASURED_GRID_AVAILABILITY_NOT_IN_DAILY_PATH":
      return "Not measured — grid availability is computed over the live day only; no completed day holds it, and another availability's figure will not stand in for it.";
    case "CANNOT_CHECK_REGISTRY_UNREADABLE":
      return "Cannot check — the registry could not be read, so an empty result cannot be read as nothing.";
    case "CANNOT_CHECK_NO_PACK_DRY_RUN":
      return "Cannot check — a pack cannot be resolved as a test: the endpoint takes a date, not a candidate pack.";
    case "NOT_MEASURED_SIGNAL_COVERAGE_NOT_COMPUTED":
      return "Not measured — signal-level coverage is not computed for this scope.";
    case "NOT_MEASURED_FIELD_NOT_ON_BLOCK_CONTRACT":
      return "Not measured — this field is not served by the block contract.";
    case "NOT_MEASURED_PROTECTION_SETTINGS_NOT_ON_RECORD":
      return "Not recorded — protection thresholds are not on record for this unit.";

    /* ── Stores and subsystems that do not exist ── */
    case "NO_ACKNOWLEDGEMENT_STORE":
      return "Not measured — no acknowledgement store exists, so no row can carry an owner or a time.";
    case "STOPPED_CAPACITY_NOT_IN_CONTRACT":
      return "Not measured — stopped capacity is not served by this endpoint.";
    case "INCONSISTENT_SEVERITY_SPLIT":
      return "Cannot check — the severity counts disagree, so a split is not stated.";
    case "NOT_MEASURED_NO_DOCUMENT_STORE":
    case "NO_DOCUMENT_STORE":
      return "Not measured — no document store is connected.";
    case "NOT_MEASURED_NO_MAINTENANCE_SCHEDULE":
      return "Not measured — no maintenance schedule is loaded.";
    case "NOT_MEASURED_WORK_ORDERS_CARRY_NO_DEVICE":
      return "Not measured — work orders carry no device reference, so a per-device count cannot be read.";
    case "NOT_MEASURED_NO_PERFORMANCE_EVENT_CLASSIFIER":
      return "Not measured — no classifier separates performance events from faults.";
    case "NOT_MEASURED_ALLOWED_ACTIONS_NOT_RESOLVED":
    case "ALLOWED_ACTIONS_NOT_RESOLVED":
      return "Not measured — the actions allowed here have not been resolved.";
    case "NOT_MEASURED_SLD_POSITION_NOT_RECORDED":
      return "Not recorded — this device has no position on the single-line diagram.";
    case "NOT_APPLICABLE_NO_OFFLINE_SYNC":
      return "Not applicable — there is no offline sync subsystem to report.";

    default:
      return humanise(reason.template);
  }
}

/** Compact age: 45s, 12m, 9h 24m, 3d 4h (paired with the absolute time on demand). */
export function formatAge(seconds: number): string {
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  if (h < 24) return remM ? `${h}h ${remM}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH ? `${d}d ${remH}h` : `${d}d`;
}

function humanise(template: string): string {
  const s = template.toLowerCase().replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1) + ".";
}

/* ────────────────────────────────────────────────────────────────────────────
   The Home brief (H1 annotation 5): "what changed, in words a person with no
   solar background can read in five seconds."

   The server sends CLAUSES — a template key and typed params — never prose
   (UX-003 §3.8). This turns one clause into one sentence; the screen joins them.
   A clause whose figure is withheld says so in words rather than being dropped,
   because a brief that silently omits what it could not read reads as good news.
   ──────────────────────────────────────────────────────────────────────────── */

export interface BriefPart {
  template: string;
  params?: Record<string, unknown> | null;
}

const n0 = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const n2 = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

/** A number and its unit, at the same display scale the figures use. */
function scaled(value: number, unit: string): string {
  const f = atDisplayScale({ value, unit } as Figure);
  const v = typeof f?.value === "number" ? f.value : value;
  const u = f?.unit ?? unit;
  return `${(u === unit ? n0 : n2).format(v)} ${u}`.trim();
}

/** Site identifiers, listed plainly. Never a site NAME (CLAUDE.md rule 5). */
function siteList(ids: unknown): string {
  const list = Array.isArray(ids) ? ids.map((x) => String(x)) : [];
  if (list.length === 0) return "";
  if (list.length === 1) return list[0] as string;
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

/** One sentence for one brief clause, or null when the key is unknown. */
export function briefText(part: BriefPart | null | undefined): string | null {
  if (!part?.template) return null;
  const p = (part.params ?? {}) as Record<string, unknown>;
  const num = (k: string): number => Number(p[k] ?? 0);

  switch (part.template) {
    case "BRIEF_FLEET_POWER": {
      // Shown at the same scale as the tile above it. The sentence and the tile
      // are the same figure, so they must not print it in two different units.
      const pct = n0.format(num("pct_of_ac"));
      const power = scaled(num("power_kw"), String(p["unit"] ?? ""));
      return `The fleet is at ${pct} percent of its AC rating, ${power} across ${num("sites")} of ${num("sites_total")} ${plural(num("sites_total"), "site", "sites")}.`;
    }
    case "BRIEF_FLEET_POWER_WITHHELD":
      return `Fleet power cannot be stated: ${reasonText({ template: String(p["reason"] ?? ""), params: (p["reason_params"] ?? {}) as Record<string, unknown> }) ?? "the reading could not be made"}`;
    case "BRIEF_ENERGY_TODAY": {
      const delivered = scaled(num("energy_kwh"), String(p["unit"] ?? ""));
      const basis = String(p["basis"] ?? "");
      const how = basis ? ` on the ${basisLabel(basis) ?? basis.toLowerCase()} basis` : "";
      return `${delivered} has been delivered so far${how}, across ${num("sites")} of ${num("sites_total")} ${plural(num("sites_total"), "site", "sites")}.`;
    }
    case "BRIEF_ENERGY_TODAY_WITHHELD":
      return `Delivered energy cannot be stated: ${reasonText({ template: String(p["reason"] ?? ""), params: (p["reason_params"] ?? {}) as Record<string, unknown> }) ?? "the reading could not be made"}`;
    case "BRIEF_DATA_PATHS_DOWN": {
      const sites = Array.isArray(p["sites"]) ? (p["sites"] as Record<string, unknown>[]) : [];
      const named = sites
        .map((s) => {
          const since = s["down_since"];
          return typeof since === "string" && since
            ? `${String(s["site_id"])} since ${since.slice(11, 16)}`
            : String(s["site_id"]);
        })
        .join(", ");
      const c = num("count");
      return `${c} data ${plural(c, "path is", "paths are")} down${named ? ` (${named})` : ""}.`;
    }
    case "BRIEF_DATA_PATHS_DOWN_ENERGY_CANNOT_CHECK":
      return `Their energy cannot be checked.`;
    case "BRIEF_DATA_PATHS_ALL_UP":
      return `All ${num("sites")} data ${plural(num("sites"), "path is", "paths are")} up.`;
    case "BRIEF_DATA_PATHS_UNCHECKED": {
      const c = num("count");
      return `${c} ${plural(c, "site", "sites")} could not be checked at all (${siteList(p["sites"])}), so nothing above counts ${plural(c, "it", "them")}.`;
    }
    case "BRIEF_DEVICES_NOT_REPORTING": {
      const c = num("sites");
      return `Some equipment is not reporting at ${c} ${plural(c, "site", "sites")} (${siteList(p["site_ids"])}).`;
    }
    case "BRIEF_LOW_PR_SITES": {
      const c = num("sites");
      return `${c} ${plural(c, "site is", "sites are")} performing below ${n0.format(num("threshold_pct"))} percent (${siteList(p["site_ids"])}).`;
    }
    case "BRIEF_ALERTS_OPEN": {
      const open = num("open");
      const ack = num("acknowledged");
      const where = `${num("sites")} ${plural(num("sites"), "site", "sites")}`;
      const ackText = ack === 0 ? "none is acknowledged" : `${ack} ${plural(ack, "is", "are")} acknowledged`;
      return `${open} device ${plural(open, "alert is", "alerts are")} open at ${where} — ${num("critical")} critical, ${num("warning")} warning — and ${ackText}.`;
    }
    case "BRIEF_ALERTS_NONE":
      return `No device alerts are open at any of the ${num("sites")} ${plural(num("sites"), "site", "sites")}.`;
    case "BRIEF_ALERTS_CANNOT_CHECK":
      return `Device alerts could not be counted: ${reasonText({ template: String(p["reason"] ?? "") }) ?? "the store could not be read"}`;
    case "BRIEF_REMAINING_SITES_NORMAL": {
      const c = num("count");
      if (c === 0) return null;
      return `The other ${c} ${plural(c, "site is", "sites are")} within normal range.`;
    }
    case "BRIEF_NOTHING_DETECTED":
      return `Nothing needed attention across the ${num("sites_total")} ${plural(num("sites_total"), "site", "sites")}.`;
    default:
      return null;
  }
}

/* ────────────────────────────────────────────────────────────────────────────
   Display scale.

   A fleet figure arrives in the unit the engine works in — kW, kWh, kWp — and
   at fleet size that prints as six digits where the drawings print three. This
   scales the PRESENTATION only: the value and its unit move together, so the
   figure still says what it is, and nothing is rounded into or out of
   existence. It is not a conversion of the underlying figure and never changes
   a ratio, a percentage or a count.

   This lives here because grammar is the one place a unit word may be written
   (UX-003): a screen must not invent one.
   ──────────────────────────────────────────────────────────────────────────── */
const SCALE_UP: Record<string, string> = {
  kW: "MW", MW: "GW", kWh: "MWh", MWh: "GWh", kWp: "MWp", MWp: "GWp",
};

/** The same figure, shown at the scale the drawings use. Steps up a tier at a
 *  time while the value stays a thousand or more and a bigger unit exists, so a
 *  fleet in kWh prints as MWh and, past a gigawatt-hour, as GWh. Withheld stays
 *  withheld. */
export function atDisplayScale(figure: Figure | undefined): Figure | undefined {
  if (!figure || typeof figure.value !== "number") return figure;
  let value = figure.value;
  let unit = figure.unit ?? "";
  // Below a thousand the smaller unit is the more informative one: 840 kW reads
  // better than 0.8 MW, and the drawings show it that way too.
  while (Math.abs(value) >= 1000 && SCALE_UP[unit]) {
    value /= 1000;
    unit = SCALE_UP[unit]!;
  }
  return unit === (figure.unit ?? "") ? figure : { ...figure, value, unit };
}

/** The series form of the same rule: ONE factor for every curve sharing an axis,
 *  so the lines stay comparable and the axis matches the figures above it. The
 *  decision is made by the largest value drawn, so a whole chart moves together
 *  or not at all. */
export function seriesDisplayScale(
  unit: string | undefined, curves: readonly (readonly (number | null)[])[],
): { factor: number; unit: string } {
  const bigger = SCALE_UP[unit ?? ""];
  if (!bigger) return { factor: 1, unit: unit ?? "" };
  let peak = 0;
  for (const c of curves) {
    for (const v of c) {
      if (typeof v === "number" && Number.isFinite(v) && Math.abs(v) > peak) peak = Math.abs(v);
    }
  }
  return peak >= 1000 ? { factor: 1000, unit: bigger } : { factor: 1, unit: unit ?? "" };
}

/** One scale for a COLUMN of figures that share a quantity — a loss cascade, a
 *  ladder, any table where the rows are steps of the same energy.
 *
 *  Scaling each row on its own is what produces a column reading 82.93 MWh,
 *  17.09 kWh, -530.16 kWh, 75.97 MWh: every cell defensible, the column
 *  unreadable, and the relative size of the rows — which is the only thing a
 *  cascade is for — destroyed. This is `seriesDisplayScale`'s rule (one factor
 *  for everything sharing an axis) applied to a table instead of a chart: the
 *  largest row decides, and the whole column moves with it or not at all.
 *
 *  A withheld figure passes through untouched; it has no value to scale and no
 *  unit to print. */
export function figuresAtOneScale(
  figures: readonly (Figure | undefined)[],
): { unit: string; figures: (Figure | undefined)[] } {
  const base = figures.find((f) => f?.unit)?.unit ?? "";
  const values = figures.map((f) => (typeof f?.value === "number" ? f.value : null));
  const { factor, unit } = seriesDisplayScale(base, [values]);
  return {
    unit,
    figures: figures.map((f) =>
      f && typeof f.value === "number" ? { ...f, value: f.value / factor, unit } : f,
    ),
  };
}

/** A curve divided by a `seriesDisplayScale` factor. Withheld stays withheld. */
export function atSeriesScale(
  curve: readonly (number | null)[], factor: number,
): (number | null)[] {
  return factor === 1
    ? [...curve]
    : curve.map((v) => (typeof v === "number" ? v / factor : null));
}
